import { useMemo, useState } from "react";
import {
  Button,
  type ButtonProps,
  Icon,
  InputText,
  List,
  ListItem,
  Popover,
  Spinner,
  Stack,
  Text,
} from "@bookingcom/bui-react";
import { ArrowNavDownIcon, CheckmarkFillIcon, MagnifyingGlassIcon } from "@bookingcom/bui-assets-react/streamline";
import { useDebounce } from "../../hooks/useDebounce";
import styles from "./SearchablePicker.module.css";

export type SearchablePickerOption = {
  id: string;
  label: string;
  sublabel?: string;
};

export type SearchablePickerLayout = "fixed" | "fluid";

type SearchablePickerProps = {
  options: SearchablePickerOption[];
  value: string | null;
  onChange: (id: string) => void;
  label: string;
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  layout: SearchablePickerLayout;
  sortSelectedFirst?: boolean;
  triggerAriaLabel?: string;
  listTestId?: string;
};

export const SearchablePicker = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  isLoading,
  layout,
  sortSelectedFirst = false,
  triggerAriaLabel,
  listTestId = "SearchablePickerList",
}: SearchablePickerProps) => {
  const [active, setActive] = useState(false);
  const [input, setInput] = useState("");
  const query = useDebounce(input, 300);

  const filteredOptions = useMemo(() => {
    const matched = !query
      ? options
      : options.filter((option) => {
          const lower = query.toLowerCase();
          return option.label.toLowerCase().includes(lower) || option.sublabel?.toLowerCase().includes(lower);
        });

    if (!sortSelectedFirst || !value) return matched;

    const selected = matched.find((option) => option.id === value);
    if (!selected) return matched;

    return [selected, ...matched.filter((option) => option.id !== value)];
  }, [query, options, sortSelectedFirst, value]);

  const selectedOption = useMemo(() => options.find((option) => option.id === value), [options, value]);

  const handleSelect = (option: SearchablePickerOption) => {
    onChange(option.id);
    setActive(false);
    setInput("");
  };

  const handleClose = () => {
    setActive(false);
    setInput("");
  };

  const isFixed = layout === "fixed";
  const isDisabled = isLoading || options.length === 0;

  return (
    <Stack gap={1}>
      <Text variant="emphasized_2">{label}</Text>
      <Popover
        closeAriaLabel="Close dropdown"
        position={isFixed ? "bottom-start" : "bottom-stretch"}
        forcePosition={true}
        hideArrow={true}
        hideClose={true}
        keepMounted={false}
        size={isFixed ? "stretch" : undefined}
        fill
        active={active}
        onOpen={() => setActive(true)}
        onClose={handleClose}
      >
        <Popover.Trigger>
          {(attributes: ButtonProps["attributes"]) => (
            <Button
              size="large"
              variant="secondary-neutral"
              icon={isLoading ? <Spinner size="small" /> : ArrowNavDownIcon}
              iconPosition="end"
              ariaLabel={triggerAriaLabel}
              className={isFixed ? styles.triggerFixed : styles.triggerFluid}
              attributes={attributes}
              disabled={isDisabled}
            >
              {selectedOption ? (
                <Text variant="emphasized_2">{selectedOption.label}</Text>
              ) : (
                <Text variant="emphasized_2" color="disabled">
                  {placeholder}
                </Text>
              )}
            </Button>
          )}
        </Popover.Trigger>
        <Popover.Content>
          <Stack gap={0}>
            <Stack mixin={{ paddingBlockStart: 4, paddingBlockEnd: 3, paddingInlineStart: 4, paddingInlineEnd: 4 }}>
              <InputText
                className={styles.searchInput}
                name="searchable-picker-search"
                placeholder={searchPlaceholder}
                startIcon={<Icon svg={MagnifyingGlassIcon} />}
                value={input}
                onChange={({ value: next }) => setInput(next)}
              />
            </Stack>
            {filteredOptions.length === 0 ? (
              <Stack mixin={{ paddingBlockStart: 3, paddingBlockEnd: 3, paddingInlineStart: 4, paddingInlineEnd: 4 }}>
                <Text variant="body_2" color="neutral_alt">
                  {emptyMessage}
                </Text>
              </Stack>
            ) : (
              <List
                className={isFixed ? styles.listFixed : styles.listFluid}
                aria-label={`${label} options`}
                attributes={{ "data-testid": listTestId }}
              >
                {filteredOptions.map((option) => (
                  <ListItem
                    key={option.id}
                    spacing="small"
                    onClick={() => handleSelect(option)}
                    className={styles.listItem}
                    aria-selected={value === option.id}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" gap={2}>
                        {option.sublabel && <Text color="neutral_alt">{option.sublabel}</Text>}
                        <Text>{option.label}</Text>
                      </Stack>
                      {value === option.id && <Icon svg={CheckmarkFillIcon} />}
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </Popover.Content>
      </Popover>
    </Stack>
  );
};
